import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faBuilding,
  faLayerGroup,
  faSquare,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import RepairTypeBadge from "./RepairTypeBadge";
import RepairCostDetailModal from "./RepairCostDetailModal";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstPositiveNumber = (...values) => {
  for (const value of values) {
    const numericValue = toFiniteNumber(value);
    if (numericValue != null && numericValue > 0) {
      return numericValue;
    }
  }
  return 0;
};

/**
 * SimulationResultsTable Component
 *
 * Displays simulation results in an expanded tabular format similar to Excel:
 * - Hierarchical rows (Bridge → Span → Maintenance → Activity)
 * - Years as columns
 * - All costs visible at once
 * - Expandable/collapsible sections
 */
const SimulationResultsTable = ({
  data,
  expandedItems,
  onToggleExpanded,
  searchTerm = "",
  filterType = "all",
  showDecayPercent = false,
  expandAllSignal = 0,
  simulationResults = null, // Full simulation results for detailed data
  mobilizationData = null,
  d610Settings = {},
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandAllWasCapped, setExpandAllWasCapped] = useState(false);
  const [selectedRepairData, setSelectedRepairData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const scrollContainerRef = useRef(null);
  const scrollRafRef = useRef(null);
  const MAX_EXPAND_ALL_ROWS = 5000;
  const ROW_HEIGHT = 72;
  const HEADER_HEIGHT = 48;
  const MOBILIZATION_HEIGHT = 72;
  const FOOTER_HEIGHT = 48;
  const OVERSCAN_ROWS = 10;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatArea = (area) => {
    return `${parseFloat(area).toLocaleString()} m²`;
  };

  // Extract all unique years from the data
  const allYears = useMemo(() => {
    const years = new Set();

    const extractYears = (item) => {
      if (item.years && item.years.length > 0) {
        item.years.forEach((year) => years.add(year.year));
      }
      if (item.children) {
        item.children.forEach((child) => extractYears(child));
      }
    };

    if (data) {
      extractYears(data);
    }

    return Array.from(years).sort();
  }, [data]);

  const toPositiveInt = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  };

  const normalizedStartYear = useMemo(() => {
    const y = Number(simulationResults?.simulationStartYear);
    return Number.isFinite(y) && y > 0 ? y : new Date().getFullYear();
  }, [simulationResults?.simulationStartYear]);

  const spanResultsIndexBySimYear = useMemo(() => {
    const idx = new Map();
    const years = Array.isArray(simulationResults?.yearlyResults)
      ? simulationResults.yearlyResults
      : [];

    years.forEach((yr, i) => {
      const simYear = Number(yr?.year || i + 1);
      const bySpanId = new Map();

      (Array.isArray(yr?.spanResults) ? yr.spanResults : []).forEach((sr) => {
        const srId =
          toPositiveInt(sr?.spanId) ||
          toPositiveInt(sr?.span_id) ||
          toPositiveInt(sr?.elementId) ||
          toPositiveInt(sr?.element_id);
        if (srId) bySpanId.set(srId, sr);
      });

      idx.set(simYear, {
        yearResult: yr,
        bySpanId,
      });
    });

    return idx;
  }, [simulationResults]);

  // Sum cost for a single year without double-counting parent rows that already aggregate children.
  const sumLeafStructuralCostsForYear = useCallback((item, year) => {
    const ownYearCost = (() => {
      if (!item.years) return 0;
      const yearData = item.years.find((y) => y.year === year);
      return yearData && typeof yearData.cost === "number" ? yearData.cost : 0;
    })();

    // Maintenance/activity rows already carry their own yearly totals.
    if (item.type === "maintenance" || item.type === "activity") {
      if (ownYearCost > 0) return ownYearCost;

      // Fallback for malformed data where parent total is missing.
      let childSum = 0;
      if (item.children) {
        item.children.forEach((child) => {
          childSum += sumLeafStructuralCostsForYear(child, year);
        });
      }
      return childSum;
    }

    const structuralChildren =
      item.children &&
      item.children.filter(
        (child) => child.type !== "maintenance" && child.type !== "activity",
      );
    const hasStructuralChildren =
      structuralChildren && structuralChildren.length > 0;

    if (hasStructuralChildren) {
      let sum = 0;
      structuralChildren.forEach((child) => {
        sum += sumLeafStructuralCostsForYear(child, year);
      });
      return sum;
    }

    // Leaf structural node: prefer own span cost. If absent, use maintenance/activity breakdown.
    if (ownYearCost > 0) {
      return ownYearCost;
    }

    let fallbackTotal = 0;
    if (item.children) {
      item.children.forEach((child) => {
        if (child.type === "maintenance" || child.type === "activity") {
          fallbackTotal += sumLeafStructuralCostsForYear(child, year);
        }
      });
    }
    return fallbackTotal;
  }, []);

  // Calculate totals for each year (used for cell display and Grand Total)
  const calculateYearTotals = useCallback(
    (item, year) => {
      const structuralChildren =
        item.children &&
        item.children.filter(
          (child) => child.type !== "maintenance" && child.type !== "activity",
        );
      const hasStructuralChildren =
        structuralChildren && structuralChildren.length > 0;

      if (hasStructuralChildren) {
        // Parent: sum from all leaf structural descendants so upper levels are always correct
        return sumLeafStructuralCostsForYear(item, year);
      }

      // Leaf node: use own cost + maintenance children
      return sumLeafStructuralCostsForYear(item, year);
    },
    [sumLeafStructuralCostsForYear],
  );

  // Per-portion curves for a split element. Only leaf area-data rows carry
  // them; parents aggregate a single number and are left alone.
  const getYearPortionDegradations = useCallback((item, year) => {
    if (!Array.isArray(item?.years)) return null;
    const yearData = item.years.find((y) => y.year === year);
    const portions = yearData?.portionDegradations;
    return Array.isArray(portions) && portions.length > 1 ? portions : null;
  }, []);

  // Get degradation percent for a specific item and year
  const getYearDegradationPercent = useCallback((item, year) => {
    if (item.years) {
      const yearData = item.years.find((y) => y.year === year);
      if (yearData && yearData.degradationPercent !== undefined) {
        return yearData.degradationPercent;
      }
    }

    // If this item doesn't have degradation data, check children
    // (for aggregated levels, we might want to show the average or max)
    if (item.children && item.children.length > 0) {
      const childDegradations = item.children
        .map((child) => getYearDegradationPercent(child, year))
        .filter((val) => val !== null && val !== undefined && val > 0);

      if (childDegradations.length > 0) {
        // Return the maximum degradation percent among children
        return Math.max(...childDegradations);
      }
    }

    return null;
  }, []);

  const matchesSearch = useCallback((item, searchTerm) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();

    // Check if this item matches
    const itemMatches =
      item.name?.toLowerCase().includes(search) ||
      item.code?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search);

    // If the item itself matches, it's a match
    if (itemMatches) return true;

    // Check if any child matches (recursive)
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => matchesSearch(child, searchTerm));
    }

    return false;
  }, []);

  const matchesFilter = useCallback((item, filterType) => {
    if (filterType === "all") return true;
    return item.type === filterType;
  }, []);

  const toggleRowExpansion = (rowId, event) => {
    // Prevent any scroll behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case "bridge":
        return faBuilding;
      case "asset":
        return faLayerGroup;
      case "span":
        return faSquare;
      case "panelbay":
        return faSquare;
      case "maintenance":
        return faChartLine;
      case "activity":
        return faChartLine;
      default:
        return faSquare;
    }
  };

  const getRowStyle = (level) => {
    switch (level) {
      case 0:
        return "bg-blue-50 border-blue-200";
      case 1:
        return "bg-green-50 border-green-200";
      case 2:
        return "bg-yellow-50 border-yellow-200";
      case 3:
        return "bg-purple-50 border-purple-200";
      case 4:
        return "bg-gray-50 border-gray-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const getTextColor = (level) => {
    switch (level) {
      case 0:
        return "text-blue-900";
      case 1:
        return "text-green-900";
      case 2:
        return "text-yellow-900";
      case 3:
        return "text-purple-900";
      case 4:
        return "text-gray-900";
      default:
        return "text-gray-900";
    }
  };

  // Helper function to aggregate multiple span results into one
  const aggregateSpanResults = (spanResults, parentItem, year) => {
    if (!spanResults || spanResults.length === 0) return null;
    if (spanResults.length === 1) return spanResults[0];

    // Aggregate all span results
    const aggregated = {
      spanId: null, // Parent doesn't have a single span ID
      spanCode: parentItem.code || parentItem.name || "Aggregated",
      spanName: parentItem.name || parentItem.code || "Aggregated Elements",
      year: year,
      totalCost: 0,
      costA: 0,
      costB: 0,
      costC: 0,
      areaA: 0,
      areaB: 0,
      areaC: 0,
      degradationPercent: 0,
      repairProcessesA: [],
      repairProcessesB: [],
      repairProcessesC: [],
      repairType: null,
      corrosionCategory: null,
      yearsSinceRepair: 0,
      repairTriggered: false,
    };

    // Track unique processes by name
    const processesMapA = new Map();
    const processesMapB = new Map();
    const processesMapC = new Map();
    let totalDegradation = 0;
    let countWithDegradation = 0;

    spanResults.forEach((sr) => {
      // Sum costs
      aggregated.totalCost += sr.totalCost || 0;
      aggregated.costA += Number(sr.costA ?? sr.cost_a ?? 0);
      aggregated.costB += Number(sr.costB ?? sr.cost_b ?? 0);
      aggregated.costC += Number(sr.costC ?? sr.cost_c ?? 0);

      // Sum areas
      aggregated.areaA += Number(sr.areaA ?? sr.area_a ?? 0);
      aggregated.areaB += Number(sr.areaB ?? sr.area_b ?? 0);
      aggregated.areaC += Number(sr.areaC ?? sr.area_c ?? 0);

      // Track degradation (average)
      if (
        sr.degradationPercent !== undefined &&
        sr.degradationPercent !== null
      ) {
        totalDegradation += sr.degradationPercent;
        countWithDegradation++;
      }

      // Merge unique processes
      [...(sr.repairProcessesA || [])].forEach((p) => {
        const name = p.processName || p.name || "Unknown";
        if (!processesMapA.has(name)) {
          processesMapA.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999,
          });
        }
      });

      [...(sr.repairProcessesB || [])].forEach((p) => {
        const name = p.processName || p.name || "Unknown";
        if (!processesMapB.has(name)) {
          processesMapB.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999,
          });
        }
      });

      [...(sr.repairProcessesC || [])].forEach((p) => {
        const name = p.processName || p.name || "Unknown";
        if (!processesMapC.has(name)) {
          processesMapC.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999,
          });
        }
      });

      // Use first non-null repair type
      if (!aggregated.repairType && sr.repairType) {
        aggregated.repairType = sr.repairType;
      }

      // Use first non-null corrosion category
      if (!aggregated.corrosionCategory && sr.corrosionCategory) {
        aggregated.corrosionCategory = sr.corrosionCategory;
      }

      // Repair triggered if any child has it
      if (sr.repairTriggered) {
        aggregated.repairTriggered = true;
      }
    });

    // Convert process maps to arrays and sort by sequence order
    aggregated.repairProcessesA = Array.from(processesMapA.values()).sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder,
    );
    aggregated.repairProcessesB = Array.from(processesMapB.values()).sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder,
    );
    aggregated.repairProcessesC = Array.from(processesMapC.values()).sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder,
    );

    // Calculate average degradation
    if (countWithDegradation > 0) {
      aggregated.degradationPercent = totalDegradation / countWithDegradation;
    }

    return aggregated;
  };

  // Helper function to find span result data for a given item and year
  const findSpanResult = (item, year) => {
    if (!simulationResults || !simulationResults.yearlyResults) return null;

    // Table year is calendar year; yearlyResults uses simulation year (1, 2, 3...)
    // Use simulation start year (Default Year 1 from settings) or current year
    const simulationYear = year - normalizedStartYear + 1;

    if (
      simulationYear < 1 ||
      simulationYear > (simulationResults.timeHorizonYears || 30)
    ) {
      return null;
    }

    // Find the year result by simulation year
    const indexedYear = spanResultsIndexBySimYear.get(simulationYear);
    const yearResult = indexedYear?.yearResult;
    if (!yearResult || !yearResult.spanResults) return null;

    // Strategy 1: Direct ID match (item.id should match spanId for span-level nodes)
    // This is the most reliable method for span-level nodes
    if (item.id) {
      const spanResult = indexedYear?.bySpanId?.get(toPositiveInt(item.id));
      if (spanResult && spanResult.totalCost > 0) return spanResult;
    }

    // Strategy 2: For activity/maintenance nodes, use parent span id (item.id is span id) so badge matches row
    if (item.type === "activity" || item.type === "maintenance") {
      if (item.id) {
        const spanResultForParent = indexedYear?.bySpanId?.get(
          toPositiveInt(item.id),
        );
        if (spanResultForParent && spanResultForParent.totalCost > 0)
          return spanResultForParent;
      }
      const spanWithCost = yearResult.spanResults.find(
        (sr) => sr.totalCost > 0,
      );
      if (spanWithCost) return spanWithCost;
    }

    // Strategy 3: Try to find by code/name match (exact match only for leaf nodes)
    // Only use this for nodes that are likely leaf nodes (no children or only maintenance children)
    const isLeafNode =
      !item.children ||
      item.children.length === 0 ||
      item.children.every(
        (child) => child.type === "maintenance" || child.type === "activity",
      );

    if (isLeafNode && (item.code || item.name)) {
      const searchCode = item.code || item.name;
      // Try exact match first
      let spanResult = yearResult.spanResults.find((sr) => {
        const codeMatch =
          sr.spanCode === searchCode || sr.spanCode === searchCode.toString();
        const nameMatch =
          sr.spanName === searchCode || sr.spanName === item.name;
        return (codeMatch || nameMatch) && sr.totalCost > 0;
      });
      if (spanResult) return spanResult;

      // For leaf nodes, try code starts with match (but only if it's a direct match, not deep hierarchy)
      spanResult = yearResult.spanResults.find((sr) => {
        if (!sr.spanCode) return false;
        // Only match if codes are very similar (exact or one level difference)
        const codeStartsWith = sr.spanCode.startsWith(searchCode);
        if (!codeStartsWith) return false;

        // Check if the remaining part is short (suggests direct relationship)
        const remaining = sr.spanCode.substring(searchCode.length);
        // Allow if remaining is empty, or starts with separator and has short remainder
        return (
          remaining.length === 0 ||
          (remaining.startsWith("-") && remaining.split("-").length <= 2)
        );
      });
      if (spanResult && spanResult.totalCost > 0) return spanResult;
    }

    // Strategy 4: For aggregated rows (bridge, plinth, pillar, etc.), find spans that belong to this parent
    // Traverse children to find actual span nodes and match their span results
    if (item.type === "bridge" || (item.children && item.children.length > 0)) {
      // Helper function to recursively find all span IDs in the subtree (leaf nodes with IDs)
      const findSpanIdsInSubtree = (node) => {
        const spanIds = [];

        // If this node has an ID and no children (or only maintenance/activity children), it's a leaf span node
        const hasStructuralChildren =
          node.children &&
          node.children.some(
            (child) =>
              child.type !== "maintenance" && child.type !== "activity",
          );

        if (node.id && !hasStructuralChildren) {
          // This is a leaf node (actual span), add its ID
          spanIds.push(node.id);
        }

        // Recursively check structural children (skip maintenance/activity nodes)
        if (node.children && node.children.length > 0) {
          node.children.forEach((child) => {
            // Only traverse structural children (not maintenance/activity)
            if (child.type !== "maintenance" && child.type !== "activity") {
              const childSpanIds = findSpanIdsInSubtree(child);
              spanIds.push(...childSpanIds);
            }
          });
        }

        return spanIds;
      };

      // Find all span IDs that belong to this parent's subtree
      const spanIdsInSubtree = findSpanIdsInSubtree(item);

      // Find span results that match any of these span IDs
      if (spanIdsInSubtree.length > 0) {
        const matchingSpanResults = yearResult.spanResults.filter(
          (sr) => spanIdsInSubtree.includes(sr.spanId) && sr.totalCost > 0,
        );

        if (matchingSpanResults.length > 0) {
          // Helper to find direct children span IDs (not recursive)
          const findDirectChildSpanIds = (parentItem) => {
            if (!parentItem.children) return [];
            return parentItem.children
              .filter(
                (child) =>
                  child.type !== "maintenance" && child.type !== "activity",
              )
              .map((child) => {
                // If child has no structural children, it's a leaf span
                const hasStructuralChildren =
                  child.children &&
                  child.children.some(
                    (c) => c.type !== "maintenance" && c.type !== "activity",
                  );
                if (child.id && !hasStructuralChildren) {
                  return child.id;
                }
                return null;
              })
              .filter((id) => id !== null);
          };

          // First, try to find span results from direct children only
          const directChildSpanIds = findDirectChildSpanIds(item);
          if (directChildSpanIds.length > 0) {
            const directChildResults = matchingSpanResults.filter((sr) =>
              directChildSpanIds.includes(sr.spanId),
            );
            if (directChildResults.length > 0) {
              // For parent nodes, aggregate all children's span results
              // This shows combined data from all contributing children
              return aggregateSpanResults(directChildResults, item, year);
            }
          }

          // If no direct children found, try to find spans that match the parent's code pattern
          // This helps when parent code is like "PLINTH-05" and we want spans like "PLINTH-05-PIL-04-..."
          if (item.code || item.name) {
            const parentCode = item.code || item.name;
            // Find spans whose code starts with parent code followed by a separator (dash, underscore, etc.)
            const patternMatchResults = matchingSpanResults.filter((sr) => {
              if (!sr.spanCode) return false;
              // Check if span code starts with parent code followed by a separator
              const startsWithParent = sr.spanCode.startsWith(parentCode);
              if (!startsWithParent) return false;

              // Check if there's a separator after the parent code (not just a longer code)
              const remaining = sr.spanCode.substring(parentCode.length);
              return (
                remaining.startsWith("-") ||
                remaining.startsWith("_") ||
                remaining.length === 0
              );
            });

            if (patternMatchResults.length > 0) {
              // Aggregate all matching results
              return aggregateSpanResults(patternMatchResults, item, year);
            }
          }

          // Fallback: If no pattern match, aggregate all matching results (deep descendants)
          return aggregateSpanResults(matchingSpanResults, item, year);
        }
      }

      // Fallback: if no match found in subtree, try to find by code/name pattern
      // This handles cases where the hierarchy structure might not perfectly match
      if (item.code || item.name) {
        const codePattern = item.code || item.name;
        const matchingSpanResults = yearResult.spanResults.filter((sr) => {
          // More specific matching: code should start with pattern or pattern should start with code
          const codeStartsWith =
            codePattern &&
            sr.spanCode &&
            (sr.spanCode.startsWith(codePattern) ||
              codePattern.startsWith(sr.spanCode));
          const nameIncludes =
            item.name &&
            sr.spanName &&
            (sr.spanName.includes(item.name) ||
              item.name.includes(sr.spanName));
          return (codeStartsWith || nameIncludes) && sr.totalCost > 0;
        });

        if (matchingSpanResults.length > 0) {
          // Return the one with highest cost
          return matchingSpanResults.reduce((max, current) =>
            current.totalCost > max.totalCost ? current : max,
          );
        }
      }

      // Last fallback for parent nodes: return first span with cost (should rarely happen)
      const spanWithCost = yearResult.spanResults.find(
        (sr) => sr.totalCost > 0,
      );
      if (spanWithCost) return spanWithCost;
    }

    // Strategy 5: Last resort - find any span result with cost > 0 for this year
    // This ensures we can show some data even if exact match fails
    const anySpanWithCost = yearResult.spanResults.find(
      (sr) => sr.totalCost > 0,
    );
    return anySpanWithCost || null;
  };

  const normalizeSpanResultForModal = useCallback(
    (spanResult, item, year, yearCost) => {
      if (!spanResult) return null;

      const details = spanResult.calculationDetails || {};
      const costBreakdown =
        details.costBreakdown || spanResult.costBreakdown || {};
      const isProcessRow =
        item.type === "activity" || item.type === "maintenance";

      return {
        ...spanResult,
        year,
        spanCode: spanResult.spanCode || item.code,
        spanName: spanResult.spanName || item.name,
        spanId:
          spanResult.spanId ||
          spanResult.span_id ||
          spanResult.elementId ||
          spanResult.element_id ||
          item.id,
        costA: Number(
          spanResult.costA ?? spanResult.cost_a ?? costBreakdown.costA ?? 0,
        ),
        costB: Number(
          spanResult.costB ?? spanResult.cost_b ?? costBreakdown.costB ?? 0,
        ),
        costC: Number(
          spanResult.costC ?? spanResult.cost_c ?? costBreakdown.costC ?? 0,
        ),
        areaA: Number(spanResult.areaA ?? spanResult.area_a ?? 0),
        areaB: Number(spanResult.areaB ?? spanResult.area_b ?? 0),
        areaC: Number(spanResult.areaC ?? spanResult.area_c ?? 0),
        totalArea: firstPositiveNumber(
          spanResult.totalArea,
          spanResult.total_area,
          details.totalArea,
          details.total_area,
          item.area,
          item.totalArea,
          item.surfaceArea,
        ),
        repairArea: firstPositiveNumber(
          spanResult.repairArea,
          spanResult.repair_area,
          spanResult.estimatedRepairArea,
          spanResult.estimated_repair_area,
          details.repairArea,
          details.repair_area,
          details.estimatedRepairArea,
          details.estimated_repair_area,
          Number(spanResult.areaA ?? spanResult.area_a ?? 0) +
            Number(spanResult.areaB ?? spanResult.area_b ?? 0) +
            Number(spanResult.areaC ?? spanResult.area_c ?? 0),
        ),
        estimatedPaintedAreaPercent: firstPositiveNumber(
          spanResult.estimatedPaintedAreaPercent,
          spanResult.estimated_painted_area_percent,
          details.estimatedPaintedAreaPercent,
          details.estimated_painted_area_percent,
          simulationResults?.scenario?.estimatedPaintedAreaPercent,
        ),
        damageSplitUsed:
          spanResult.damageSplitUsed ||
          spanResult.damage_split_used ||
          simulationResults?.scenario?.damageSplit ||
          null,
        clickedCellCost: isProcessRow ? yearCost : null,
        clickedRowName: isProcessRow ? item.name || item.code : null,
      };
    },
    [simulationResults],
  );

  // Helper function to determine repair type from span result (use backend label so table and modal match)
  const getRepairTypeFromResult = (spanResult) => {
    if (!spanResult) return null;

    // Prefer repairLabel from new simulation engine (WASH, TU, OC, RE)
    const label = (spanResult.repairLabel || "").toUpperCase();
    if (label === "WASH") return "wash";
    if (label === "TU") return "touchup";
    if (label === "OC") return "overcoat";
    if (label === "RE") return "recoat";

    // Use repairType from backend if available
    if (spanResult.repairType) {
      return spanResult.repairType;
    }

    // Fallback: determine from processes
    const allProcesses = [
      ...(spanResult.repairProcessesA || []),
      ...(spanResult.repairProcessesB || []),
      ...(spanResult.repairProcessesC || []),
    ].map((p) => (p.processName || p.name || "").toLowerCase());

    const hasWash = allProcesses.some((p) => p.includes("wash"));
    const hasPrime = allProcesses.some((p) => p.includes("prime"));
    const hasStripe = allProcesses.some((p) => p.includes("stripe"));
    const hasMid = allProcesses.some((p) => p.includes("mid"));
    const hasTop = allProcesses.some((p) => p.includes("top"));

    // Only wash = Wash
    if (hasWash && !hasPrime && !hasStripe && !hasMid && !hasTop) {
      return "wash";
    }
    // Only topcoat = Touch-up
    if (!hasPrime && !hasStripe && !hasMid && hasTop) {
      return "touchup";
    }
    // Multiple processes = Recoat/Full
    if (hasPrime || hasStripe || hasMid) {
      return "recoat";
    }

    return null;
  };

  const getRepairTypeFromActivityKey = (repairTypeKey) => {
    const key = (repairTypeKey || "").toUpperCase();
    if (key === "WASH") return "wash";
    if (key === "TU") return "touchup";
    if (key === "OC") return "overcoat";
    if (key === "RE") return "recoat";
    return null;
  };

  // Handle cell click
  const handleCellClick = (item, year, yearCost) => {
    if (yearCost <= 0) return; // Don't open modal for zero costs

    if (item.type === "mobilization") {
      const yearDetail = mobilizationData?.yearlyCosts?.[year] || null;
      if (!yearDetail || Number(yearDetail.totalCost || 0) <= 0) return;
      setSelectedRepairData({
        ...yearDetail,
        costType: "mobilization",
        spanCode: "PROJECT",
        spanName: "Mobilization Costs",
        spanId: "mobilization-project",
      });
      setIsModalOpen(true);
      return;
    }

    const spanResult = findSpanResult(item, year);
    if (spanResult) {
      setSelectedRepairData(
        normalizeSpanResultForModal(spanResult, item, year, yearCost),
      );
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (!data || !expandAllSignal) return;

    const rowIds = new Set();
    let capped = false;

    const queue = [{ node: data, rowId: "root" }];
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) break;

      const hasChildren =
        Array.isArray(current.node?.children) &&
        current.node.children.length > 0;

      if (!hasChildren) continue;

      rowIds.add(current.rowId);
      if (rowIds.size >= MAX_EXPAND_ALL_ROWS) {
        capped = true;
        break;
      }

      current.node.children.forEach((child, index) => {
        queue.push({
          node: child,
          rowId: `${current.rowId}-${index}`,
        });
      });
    }

    setExpandedRows(rowIds);
    setExpandAllWasCapped(capped);
  }, [data, expandAllSignal]);

  const getDirectSpanResult = useCallback(
    (item, year) => {
      const itemId = toPositiveInt(item?.id);
      if (!itemId) return null;
      const simulationYear = year - normalizedStartYear + 1;
      if (simulationYear < 1) return null;
      return (
        spanResultsIndexBySimYear.get(simulationYear)?.bySpanId?.get(itemId) ||
        null
      );
    },
    [normalizedStartYear, spanResultsIndexBySimYear],
  );

  const flattenedRows = useMemo(() => {
    if (!data) return [];
    const rows = [];

    const walk = (
      itemNode,
      level = 0,
      rowId = "root",
      parentExpanded = true,
    ) => {
      const hasChildren = itemNode.children && itemNode.children.length > 0;
      const isExpanded = expandedRows.has(rowId);
      const matchesSearchTerm = matchesSearch(itemNode, searchTerm);
      const matchesFilterType = matchesFilter(itemNode, filterType);

      if (!matchesSearchTerm || !matchesFilterType || !parentExpanded) return;

      rows.push({
        item: itemNode,
        level,
        rowId,
        isExpanded,
        hasChildren,
      });

      if (hasChildren && isExpanded) {
        itemNode.children.forEach((child, index) => {
          walk(child, level + 1, `${rowId}-${index}`, true);
        });
      }
    };

    walk(data, 0, "root", true);
    return rows;
  }, [
    data,
    expandedRows,
    searchTerm,
    filterType,
    matchesSearch,
    matchesFilter,
  ]);

  const rowMetrics = useMemo(() => {
    const metrics = new Map();

    flattenedRows.forEach(({ item, rowId, hasChildren }) => {
      const yearCosts = new Map();
      const yearDegradations = new Map();
      const yearPortionDegradations = new Map();
      const yearSpanResults = new Map();
      let totalCost = 0;

      allYears.forEach((year) => {
        const yearCost = calculateYearTotals(item, year);
        yearCosts.set(year, yearCost);
        totalCost += yearCost;

        if (showDecayPercent) {
          yearDegradations.set(year, getYearDegradationPercent(item, year));
          yearPortionDegradations.set(
            year,
            getYearPortionDegradations(item, year),
          );
        }

        const isLeafOrProcessRow =
          item.type === "activity" ||
          item.type === "maintenance" ||
          !hasChildren;

        if (yearCost > 0 && isLeafOrProcessRow) {
          yearSpanResults.set(year, getDirectSpanResult(item, year));
        } else {
          yearSpanResults.set(year, null);
        }
      });

      metrics.set(rowId, {
        yearCosts,
        yearDegradations,
        yearPortionDegradations,
        yearSpanResults,
        totalCost,
      });
    });

    return metrics;
  }, [
    flattenedRows,
    allYears,
    showDecayPercent,
    getDirectSpanResult,
    calculateYearTotals,
    getYearDegradationPercent,
    getYearPortionDegradations,
  ]);

  const getMobilizationYearCost = useCallback(
    (year) => Number(mobilizationData?.yearlyCosts?.[year]?.totalCost || 0),
    [mobilizationData],
  );

  const grandTotalsByYear = useMemo(() => {
    const totals = new Map();
    allYears.forEach((year) => {
      totals.set(
        year,
        calculateYearTotals(data, year) + getMobilizationYearCost(year),
      );
    });
    return totals;
  }, [allYears, data, calculateYearTotals, getMobilizationYearCost]);

  const grandTotal = useMemo(() => {
    return allYears.reduce(
      (sum, year) => sum + (grandTotalsByYear.get(year) || 0),
      0,
    );
  }, [allYears, grandTotalsByYear]);

  const gridTemplateColumns = useMemo(() => {
    return `40px minmax(320px, 2fr) repeat(${allYears.length}, minmax(130px, 1fr)) minmax(140px, 1.1fr)`;
  }, [allYears.length]);

  const gridMinWidthPx = useMemo(() => {
    return 40 + 320 + allYears.length * 130 + 140;
  }, [allYears.length]);

  const bodyScrollTop = Math.max(
    0,
    scrollTop - HEADER_HEIGHT - MOBILIZATION_HEIGHT,
  );
  const bodyViewportHeight = Math.max(
    ROW_HEIGHT,
    viewportHeight - HEADER_HEIGHT - FOOTER_HEIGHT - MOBILIZATION_HEIGHT,
  );
  const visibleRowCount = Math.ceil(bodyViewportHeight / ROW_HEIGHT);
  const startIndex = Math.max(
    0,
    Math.floor(bodyScrollTop / ROW_HEIGHT) - OVERSCAN_ROWS,
  );
  const endIndex = Math.min(
    flattenedRows.length,
    startIndex + visibleRowCount + OVERSCAN_ROWS * 2,
  );
  const visibleRows = flattenedRows.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(
    0,
    (flattenedRows.length - endIndex) * ROW_HEIGHT,
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateHeight = () => setViewportHeight(container.clientHeight || 0);
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleGridScroll = useCallback((event) => {
    const nextTop = event.currentTarget.scrollTop;
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      setScrollTop(nextTop);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  const renderGridRow = ({ item, level, rowId, hasChildren, isExpanded }) => {
    const metrics = rowMetrics.get(rowId);
    const totalCost = metrics?.totalCost || 0;

    return (
      <div
        key={rowId}
        className={`grid h-[72px] ${getRowStyle(level)} hover:bg-opacity-80 transition-colors border-b border-gray-200`}
        style={{ gridTemplateColumns }}
      >
        <div className="px-3 py-2 border-r border-gray-200 sticky left-0 bg-inherit z-10 flex items-center">
          {hasChildren && (
            <button
              onClick={(e) => toggleRowExpansion(rowId, e)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
              type="button"
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronDown : faChevronRight}
                className="w-4 h-4"
              />
            </button>
          )}
        </div>
        <div className="px-3 py-2 border-r border-gray-200 sticky left-[40px] bg-inherit z-10 flex items-center">
          <div className="flex items-center space-x-2 min-w-0">
            <FontAwesomeIcon
              icon={getIcon(item.type)}
              className={`w-4 h-4 ${getTextColor(level)}`}
            />
            <div className="min-w-0">
              <div className={`font-medium ${getTextColor(level)} truncate`}>
                {item.name || item.code || "Unnamed Item"}
              </div>
              {item.description && (
                <div className="text-xs text-gray-600 truncate">
                  {item.description}
                </div>
              )}
              {item.area && (
                <div className="text-xs text-gray-500 truncate">
                  Area: {formatArea(item.area)}
                </div>
              )}
            </div>
          </div>
        </div>
        {allYears.map((year) => {
          const yearCost = metrics?.yearCosts?.get(year) || 0;
          const degradationPercent = showDecayPercent
            ? metrics?.yearDegradations?.get(year)
            : null;
          const portionDegradations = showDecayPercent
            ? metrics?.yearPortionDegradations?.get(year)
            : null;
          const shouldShowDecay =
            showDecayPercent &&
            degradationPercent !== null &&
            degradationPercent > 0 &&
            (yearCost > 0 || degradationPercent >= 1.0);
          // A split element has one curve per portion; the single element-level
          // figure is only the dominant portion's and hides the one that
          // actually triggered.
          const shouldShowPortionDecay =
            showDecayPercent &&
            Array.isArray(portionDegradations) &&
            portionDegradations.some(
              (portion) => Number(portion.degradationPercent) > 0,
            ) &&
            (yearCost > 0 ||
              portionDegradations.some(
                (portion) => Number(portion.degradationPercent) >= 1.0,
              ));

          const spanResult = metrics?.yearSpanResults?.get(year) || null;
          const repairType =
            item.type === "activity"
              ? getRepairTypeFromActivityKey(item.repairTypeKey)
              : spanResult
                ? getRepairTypeFromResult(spanResult)
                : null;
          const isClickable = yearCost > 0 && spanResult !== null;

          return (
            <div
              key={`${rowId}-${year}`}
              className={`px-3 py-2 text-right border-r border-gray-200 flex items-center justify-end ${isClickable ? "cursor-pointer hover:bg-blue-50 transition-colors" : ""}`}
              onClick={() =>
                isClickable && handleCellClick(item, year, yearCost)
              }
              title={
                isClickable
                  ? "Click to view detailed cost breakdown"
                  : yearCost > 0
                    ? "No detailed data available"
                    : ""
              }
            >
              <div className="flex flex-col items-end gap-1">
                {yearCost > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-sm font-medium ${isClickable ? "text-blue-700 underline" : "text-gray-900"}`}
                    >
                      {formatCurrency(yearCost)}
                    </span>
                    {repairType && <RepairTypeBadge repairType={repairType} />}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
                {shouldShowPortionDecay ? (
                  <span className="mt-0.5 flex flex-col items-end leading-tight">
                    {portionDegradations.map((portion, portionIdx) => (
                      <span
                        key={`${rowId}-${year}-${portion.combinationCode || portionIdx}`}
                        className={`text-xs ${
                          portion.triggered
                            ? "text-amber-700 font-medium"
                            : "text-gray-500"
                        }`}
                        title={
                          portion.triggered
                            ? `${portion.combinationCode} triggered this year's repair`
                            : `${portion.combinationCode} below its threshold`
                        }
                      >
                        {portion.combinationCode}:{" "}
                        {Number(portion.degradationPercent).toFixed(2)}%
                      </span>
                    ))}
                  </span>
                ) : (
                  shouldShowDecay && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      {degradationPercent.toFixed(2)}%
                    </span>
                  )
                )}
              </div>
            </div>
          );
        })}

        <div className="px-3 py-2 text-right bg-gray-100 flex items-center justify-end">
          <span className="text-sm font-semibold text-gray-900">
            {formatCurrency(totalCost)}
          </span>
        </div>
      </div>
    );
  };

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        No simulation data available
      </div>
    );
  }

  const mobilizationGrandTotal = allYears.reduce(
    (sum, year) => sum + getMobilizationYearCost(year),
    0,
  );

  return (
    <>
      <div
        ref={scrollContainerRef}
        className="overflow-auto max-h-[70vh] border border-gray-200 rounded-lg"
        style={{ scrollBehavior: "auto" }}
        onScroll={handleGridScroll}
      >
        {expandAllWasCapped && (
          <div className="mx-2 mt-2 mb-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Expand All was limited to keep the page responsive on very large
            simulations. Use search/filter to narrow scope, then expand again.
          </div>
        )}

        <div style={{ minWidth: `${gridMinWidthPx}px` }}>
          <div
            className="grid bg-gray-100 sticky top-0 z-30 border-b border-gray-200"
            style={{ gridTemplateColumns }}
          >
            <div className="px-3 py-3 border-r border-gray-200 sticky left-0 bg-gray-100 z-40" />
            <div className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 sticky left-[40px] bg-gray-100 z-40">
              Item
            </div>
            {allYears.map((year) => (
              <div
                key={year}
                className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200"
              >
                {year}
              </div>
            ))}
            <div className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
              Total
            </div>
          </div>

          <div
            className="grid h-[72px] bg-amber-50 hover:bg-amber-100 transition-colors border-b border-gray-200"
            style={{ gridTemplateColumns }}
          >
            <div className="px-3 py-2 border-r border-gray-200 sticky left-0 bg-inherit z-10" />
            <div className="px-3 py-2 border-r border-gray-200 sticky left-[40px] bg-inherit z-10 flex items-center">
              <div>
                <div className="font-medium text-amber-900">
                  Mobilization Costs
                </div>
                <div className="text-xs text-amber-700 truncate">
                  Project-level yearly mobilization based on degraded repair
                  area
                </div>
              </div>
            </div>
            {allYears.map((year) => {
              const yearCost = getMobilizationYearCost(year);
              const clickable = yearCost > 0;
              return (
                <div
                  key={`mobilization-${year}`}
                  className={`px-3 py-2 text-right border-r border-gray-200 flex items-center justify-end ${clickable ? "cursor-pointer hover:bg-amber-200 transition-colors" : ""}`}
                  onClick={() =>
                    clickable &&
                    handleCellClick({ type: "mobilization" }, year, yearCost)
                  }
                  title={clickable ? "Click to view mobilization details" : ""}
                >
                  {yearCost > 0 ? (
                    <span className="text-sm font-medium text-amber-800 underline">
                      {formatCurrency(yearCost)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </div>
              );
            })}
            <div className="px-3 py-2 text-right bg-amber-100 flex items-center justify-end">
              <span className="text-sm font-semibold text-amber-900">
                {formatCurrency(mobilizationGrandTotal)}
              </span>
            </div>
          </div>

          <div className="bg-white">
            <div style={{ height: topSpacerHeight }} />
            {visibleRows.map((row) => renderGridRow(row))}
            <div style={{ height: bottomSpacerHeight }} />
          </div>

          <div
            className="grid bg-gray-100 border-t border-gray-200 sticky bottom-0 z-20"
            style={{ gridTemplateColumns }}
          >
            <div className="px-3 py-3 border-r border-gray-200 sticky left-0 bg-gray-100 z-30" />
            <div className="px-3 py-3 font-semibold text-gray-900 border-r border-gray-200 sticky left-[40px] bg-gray-100 z-30">
              Grand Total
            </div>
            {allYears.map((year) => {
              const yearTotal = grandTotalsByYear.get(year) || 0;
              return (
                <div
                  key={year}
                  className="px-3 py-3 text-right font-semibold text-gray-900 border-r border-gray-200"
                >
                  {formatCurrency(yearTotal)}
                </div>
              );
            })}
            <div className="px-3 py-3 text-right font-semibold text-gray-900 bg-gray-200">
              {formatCurrency(grandTotal)}
            </div>
          </div>
        </div>
      </div>

      {/* Repair Cost Detail Modal */}
      <RepairCostDetailModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRepairData(null);
        }}
        repairData={selectedRepairData}
        d610Settings={d610Settings}
      />
    </>
  );
};

export default SimulationResultsTable;
